/**
 * utils/feeEnums.ts
 * Pure utility functions for FeeEnums domain
 *
 * Parametric by design: These helpers use direct literal comparisons and have
 * zero runtime dependencies. Unlike incrementType, no map parameter is required.
 * Functions are pure: same inputs always produce same outputs.
 *
 * USAGE:
 * ```ts
 * // Import from module directly
 * import { isUserVerified, isTrustedFeeData, needsVerification } from '@/utils/feeEnums';
 *
 * // Or import from barrel (available after Phase 6)
 * import { isUserVerified, isTrustedFeeData, needsVerification } from '@/utils';
 *
 * // Usage examples with all 5 VerificationStatus values
 * isUserVerified('userVerified');    // => true
 * isTrustedFeeData('userOverride');  // => true
 * needsVerification('template');     // => true
 * needsVerification('assumed');      // => true
 * isTrustedFeeData('verified');      // => false
 * ```
 *
 * @example
 * ```ts
 * import { isUserVerified, isTrustedFeeData, needsVerification } from '@/utils/feeEnums';
 * import type { VerificationStatus } from '@/types/shared/FeeEnums';
 *
 * // Check if user explicitly verified data
 * const status: VerificationStatus = 'userVerified';
 * if (isUserVerified(status)) {
 *   console.log('User has confirmed this fee data');
 * }
 *
 * // Check if data is trustworthy for calculations
 * if (isTrustedFeeData(status)) {
 *   console.log('Safe to use in calculations');
 * }
 *
 * // Check if data needs verification
 * const templateStatus: VerificationStatus = 'template';
 * if (needsVerification(templateStatus)) {
 *   console.log('Prompt user to verify this data');
 * }
 * ```
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero imports from `constants/` or other domain modules
 * - Only `import type` from `types/` (no runtime dependencies)
 * - No side effects (no logs, no mutations, no I/O, no caches)
 * - All functions are deterministic and testable
 * - These functions use direct literal comparisons; no map parameter required
 *
 * ESLINT RULE MAINTENANCE:
 * - When adding new utility functions, update ESLint 'no-restricted-imports' rule
 * - in both main config (line ~123) and services override (line ~195) in eslint.config.js
 *
 * @since P00-S01-WU2-T05-Phase3 (2025-11-04)
 * @see src/types/shared/FeeEnums.ts
 * @see src/constants/feeEnums.ts (display maps; UI only)
 * @see src/utils/incrementType.ts (canonical parametric example)
 */

// Internal absolute imports (type-only)
import type { VerificationStatus } from '@/types/shared/FeeEnums';

/**
 * Check if verification status represents user-verified data
 *
 * Returns true only for 'userVerified' status. This indicates the user has
 * explicitly confirmed the fee data accuracy.
 *
 * @param status - VerificationStatus value to check
 * @returns true if status is 'userVerified', false otherwise
 *
 * @example
 * ```ts
 * import { isUserVerified } from '@/utils/feeEnums';
 *
 * isUserVerified('userVerified');  // => true
 * isUserVerified('userOverride');  // => false
 * isUserVerified('verified');      // => false
 * ```
 */
export function isUserVerified(status: VerificationStatus): boolean {
  return status === 'userVerified';
}

/**
 * Check if fee data comes from a trusted source
 *
 * Returns true for 'userVerified' or 'userOverride' statuses. Both indicate
 * user involvement in data validation, making them trustworthy for calculations.
 *
 * @param status - VerificationStatus value to check
 * @returns true if status is 'userVerified' or 'userOverride', false otherwise
 *
 * @example
 * ```ts
 * import { isTrustedFeeData } from '@/utils/feeEnums';
 *
 * isTrustedFeeData('userVerified');  // => true
 * isTrustedFeeData('userOverride');  // => true
 * isTrustedFeeData('template');      // => false
 * isTrustedFeeData('assumed');       // => false
 * isTrustedFeeData('verified');      // => false
 * ```
 */
export function isTrustedFeeData(status: VerificationStatus): boolean {
  return status === 'userVerified' || status === 'userOverride';
}

/**
 * Check if fee data requires user verification
 *
 * Returns true for 'template' or 'assumed' statuses. These indicate placeholder
 * or default data that should be verified by the user before relying on
 * calculations.
 *
 * @param status - VerificationStatus value to check
 * @returns true if status is 'template' or 'assumed', false otherwise
 *
 * @example
 * ```ts
 * import { needsVerification } from '@/utils/feeEnums';
 *
 * needsVerification('template');      // => true
 * needsVerification('assumed');       // => true
 * needsVerification('userVerified');  // => false
 * needsVerification('userOverride');  // => false
 * needsVerification('verified');      // => false
 * ```
 */
export function needsVerification(status: VerificationStatus): boolean {
  return status === 'template' || status === 'assumed';
}
